import React, { useState, useEffect } from 'react';
import {
  TextField, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Box, Button, Dialog, DialogTitle, DialogContent,
  TablePagination
} from '@mui/material';
import { supabase } from '../../lib/supabaseClient';
import { format } from 'date-fns';

const ScriptAnalytics = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchName, setSearchName] = useState('');
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [isProjectsDialogOpen, setIsProjectsDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch users
      const { data: usersData, error: usersError } = await supabase
        .from('user')
        .select('id, name')
        .order('name');

      if (usersError) throw usersError;

      // Fetch projects with script_uploaded = true
      const { data: projectsData, error: projectsError } = await supabase
        .from('project')
        .select('project_id, project_name, user_id, created_at')
        .eq('script_uploaded', true);

      if (projectsError) throw projectsError;

      // Process data
      const usersWithScripts = usersData.map(user => {
        const userProjects = projectsData.filter(project => project.user_id === user.id);
        
        return {
          ...user,
          scriptCount: userProjects.length,
          projects: userProjects.sort((a, b) => 
            new Date(b.created_at) - new Date(a.created_at)
          )
        };
      });

      // Sort by script count in descending order
      const sortedUsers = usersWithScripts.sort((a, b) => b.scriptCount - a.scriptCount);

      setUsers(sortedUsers);
      setFilteredUsers(sortedUsers);

    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleSearch = () => {
    const filtered = users.filter(user =>
      user.name?.toLowerCase().includes(searchName.toLowerCase())
    );
    setFilteredUsers(filtered);
    setPage(0);
  };

  const handleProjectsClick = (projects) => {
    setSelectedProjects(projects);
    setIsProjectsDialogOpen(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div className="script-analytics">
      <h2>Scripts Uploaded Per User</h2>

      {/* Search Filter */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search by Name"
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
        />
        <Button variant="contained" onClick={handleSearch}>
          Search
        </Button>
      </Box>

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>S. No.</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>No. of Scripts</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsers.map((user, index) => (
              <TableRow key={user.id}>
                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell>{user.name}</TableCell>
                <TableCell>
                  <Button
                    onClick={() => handleProjectsClick(user.projects)}
                    variant="text"
                    color="primary"
                    disabled={user.scriptCount === 0}
                  >
                    {user.scriptCount}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredUsers.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[20]}
        />
      </TableContainer>

      {/* Projects Dialog */}
      <Dialog
        open={isProjectsDialogOpen}
        onClose={() => setIsProjectsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Projects with Scripts</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>S. No.</TableCell>
                  <TableCell>Project Name</TableCell>
                  <TableCell>Creation Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedProjects.map((project, index) => (
                  <TableRow key={project.project_id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{project.project_name}</TableCell>
                    <TableCell>
                      {format(new Date(project.created_at), 'dd/MM/yyyy')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScriptAnalytics; 